import type { FC } from 'react'
import { useState } from 'react'

import { Input, MessagePopup, Toggle, Tooltip } from '@skytech/ui'
import { Button } from '@skytech/ui/Button'
import { isOnlyDigits } from '@skytech/utils'
import CogIcon from 'mdi-react/CogIcon'

import { Translation } from '@/app/translations/Translation'
import { useTranslation } from '@/app/translations/translations-hooks'

import { useUpdateProduct } from '../hooks'

type ProductSettingsProps = {
  policyId: number
  productId: number
  priceId: number | null
  isMandatory: boolean
  quantityLimit: number
  alias: string | null
  name: string
}

export const ProductSettings: FC<ProductSettingsProps> = props => {
  const [isSettingsPopupOpen, setIsSettingsPopupOpen] = useState(false)

  return (
    <div className="is-mandatory-icon">
      <Button
        className="m-0 px-1"
        size="extraSmall"
        Icon={<CogIcon />}
        onClick={e => {
          e.stopPropagation()
          setIsSettingsPopupOpen(true)
        }}
      />

      {isSettingsPopupOpen && (
        <ProductSettingsPopup
          {...props}
          isOpen={isSettingsPopupOpen}
          onClose={() => setIsSettingsPopupOpen(false)}
        />
      )}
    </div>
  )
}

export const ProductSettingsPopup: FC<
  ProductSettingsProps & { isOpen: boolean; onClose: () => void }
> = ({
  quantityLimit,
  isMandatory,
  alias,
  name,
  policyId,
  priceId,
  productId,
  isOpen,
  onClose,
}) => {
  const [quantityLimitValue, setQuantityLimitValue] = useState<number | ''>(quantityLimit)
  const [isProductMandatory, setIsProductMandatory] = useState(isMandatory)
  const [productAlias, setProductAlias] = useState(alias || name)
  const { updateProduct, isUpdating } = useUpdateProduct()
  const translate = useTranslation()

  const updatedProductAlias = !productAlias || productAlias === name ? null : productAlias

  const onRemoveProduct = () => {
    updateProduct(
      {
        policyId,
        payload: {
          products: [
            {
              productId: productId,
              isSelected: false,
              priceId: priceId,
              isMandatory,
              quantityLimit: quantityLimitValue || 1,
              alias: updatedProductAlias,
            },
          ],
        },
      },
      {
        onSuccess: onClose,
      }
    )
  }

  return (
    <MessagePopup
      variant="narrow"
      type="success"
      className="is-product-mandatory-popup"
      title={<Translation id="Product settings" />}
      isOpen={isOpen}
      onClose={onClose}
    >
      <>
        <Input
          isFullWidth
          id="alias"
          label={<Translation id="Product alias" />}
          marginBottom={16}
          value={productAlias}
          onChange={e => setProductAlias(e.target.value)}
        />

        <Input
          isAllTextSelectedOnFocus
          type="numeric"
          isFullWidth
          id="quantity-limit"
          label={<Translation id="Quantity limit" />}
          marginBottom={16}
          value={quantityLimitValue}
          onChange={e => {
            const val = e.target.value
            if (val === '') setQuantityLimitValue('')
            if (!isOnlyDigits(val)) return
            const newValue = Number(val)
            if (newValue === 0) return
            setQuantityLimitValue(newValue)
          }}
        />

        {/* todo: remove important when popup is refactored */}
        <h5 className="!mb-2 !mt-4 text-sm font-normal">
          <Translation id="Decide whether the product is selected by default while making a new order and cannot be deselected." />
        </h5>

        <Toggle
          id="is-mandatory"
          label={<Translation id="Is mandatory" />}
          checked={isProductMandatory}
          onChange={() => setIsProductMandatory(!isProductMandatory)}
        />

        <footer className="mt-4 flex items-center justify-end">
          <Button variant="tertiary" className="mr-0" onClick={onClose}>
            <Translation id="Cancel" />
          </Button>

          <Button
            variant="primary"
            isLoading={isUpdating}
            onClick={() =>
              updateProduct({
                policyId,
                payload: {
                  products: [
                    {
                      productId,
                      isMandatory: isProductMandatory,
                      priceId,
                      isSelected: true,
                      quantityLimit: quantityLimitValue || 1,
                      alias: updatedProductAlias,
                    },
                  ],
                },
              })
            }
          >
            <Translation id="Submit" />
          </Button>

          {isMandatory && (
            <Tooltip
              id={`remove-button-tooltip-${name}-${productId}`}
              style={{ zIndex: 20 }}
              size="large"
            />
          )}
          <span
            data-tooltip-id={`remove-button-tooltip-${name}-${productId}`}
            data-tooltip-content={translate(
              `A mandatory product cannot be removed. To enable removing, change the "Is mandatory" option and save`
            )}
          >
            <Button
              variant="secondary"
              onClick={onRemoveProduct}
              disabled={isUpdating || isMandatory}
            >
              <Translation id="Remove" />
            </Button>
          </span>
        </footer>
      </>
    </MessagePopup>
  )
}
